import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, IconButton, RadioGroup, FormControlLabel, Radio, } from "@mui/material";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { CalendarDate } from "@internationalized/date";
import { Dropdown, TextInput, EmailInput, NumberInput } from "../../../common_components/FormInputs";
import SingleDatePicker from "../../../common_components/daterangepicker/SingleDatePicker";
import AppButton from "../../../common_components/AppButton";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { fetchDesignationWiseUsers, selectDesignationWiseUsers, selectAllUsersList, selectUsersByDesignation, } from "../../../redux/clientDataSlice";
import { fetchAllClientList, selectAllClientList } from "../../../redux/clientDataSlice";
import { createPublishedArticle, updatePublishedArticle, selectSavingArticle, } from "../../../redux/outreachPerformanceSlice";
// Same fixed list the legacy portal used (listing.blade.php) — no backend endpoint for this, it's a static set.
const TAG_OPTIONS = [
    { value: "Organic", label: "Organic" },
    { value: "Flat Fee/CPC", label: "Flat Fee/CPC" },
    { value: "Coupon", label: "Coupon" },
    { value: "SEO", label: "SEO" },
];
const TYPE_OPTIONS = [
    { value: "paid", label: "Paid" },
    { value: "nonpaid", label: "Non-Paid" },
];
const AFFILIATE_LEAD_DESIGNATION = "Affiliate Lead";
const getInitialForm = () => ({
    closedById: "",
    accountManagerId: "",
    outreachSpecialistId: "",
    clientId: "",
    publishedDate: null,
    type: "",
    commissionRate: "",
    contactName: "",
    contactEmail: "",
    category: "",
    publication: "",
    articleLink: "",
    tags: [],
    emailNotification: "email_affiliate_team",
});
const parseToCalendarDate = (dateStr) => {
    if (!dateStr)
        return null;
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime()))
            return null;
        return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
    }
    catch {
        return null;
    }
};
export default function AddArticleModal({ open, onClose, onSaved, initialArticle, mode = "create" }) {
    const dispatch = useDispatch();
    const designationWiseUsers = useSelector(selectDesignationWiseUsers);
    const allUsers = useSelector(selectAllUsersList);
    const outreachSpecialists = useSelector(selectUsersByDesignation(AFFILIATE_LEAD_DESIGNATION));
    const allClients = useSelector(selectAllClientList);
    const saving = useSelector(selectSavingArticle);
    const [form, setForm] = useState(getInitialForm());
    const [errorText, setErrorText] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        if (!designationWiseUsers)
            dispatch(fetchDesignationWiseUsers());
        if (!allClients?.length)
            dispatch(fetchAllClientList());
    }, [open, designationWiseUsers, allClients, dispatch]);
    useEffect(() => {
        if (open) {
            if (initialArticle && (mode === "edit" || mode === "copy")) {
                setForm({
                    closedById: initialArticle.closed_by?._id || initialArticle.closed_by || "",
                    accountManagerId: initialArticle.am_id?._id || initialArticle.am_id || "",
                    outreachSpecialistId: initialArticle.outreach_specialist?._id || initialArticle.outreach_specialist || "",
                    clientId: initialArticle.client_id?._id || initialArticle.client_id || "",
                    publishedDate: parseToCalendarDate(initialArticle.published_date),
                    type: initialArticle.type || "nonpaid",
                    commissionRate: initialArticle.commission_rate !== undefined ? String(initialArticle.commission_rate) : "",
                    contactName: initialArticle.name || initialArticle.contact_name || "",
                    contactEmail: initialArticle.email || initialArticle.contact_email || "",
                    category: initialArticle.category || "",
                    publication: initialArticle.publication || "",
                    articleLink: initialArticle.article_link || "",
                    tags: Array.isArray(initialArticle.tags) ? initialArticle.tags : [],
                    emailNotification: initialArticle.email_notification || "email_affiliate_team",
                });
            }
            else {
                setForm(getInitialForm());
            }
            setErrorText(null);
        }
    }, [open, initialArticle, mode]);
    const userOptions = useMemo(() => (allUsers || []).map((u) => ({
        value: u._id,
        label: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
    })), [allUsers]);
    const outreachSpecialistOptions = useMemo(() => (outreachSpecialists || []).map((u) => ({
        value: u._id,
        label: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
    })), [outreachSpecialists]);
    const clientOptions = useMemo(() => (allClients || [])
        .filter((c) => c.status === "active" && Array.isArray(c.type) && c.type.includes("affiliate"))
        .map((c) => ({ value: c._id, label: c.name })), [allClients]);
    const setField = useCallback((key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);
    const handleSave = useCallback(async () => {
        if (!form.clientId)
            return setErrorText("Client is required.");
        if (!form.publishedDate)
            return setErrorText("Published date is required.");
        if (!form.contactName.trim())
            return setErrorText("Contact name is required.");
        if (!form.contactEmail.trim())
            return setErrorText("Contact email is required.");
        if (!form.category.trim())
            return setErrorText("Category is required.");
        if (!form.publication.trim())
            return setErrorText("Publication is required.");
        if (!form.articleLink.trim())
            return setErrorText("Article link is required.");
        if (!form.type)
            return setErrorText("Type is required.");
        if (!form.tags.length)
            return setErrorText("Tags is required.");
        setErrorText(null);
        setIsSubmitting(true);
        const payload = {
            store_id: form.clientId,
            user_id: form.closedById || undefined,
            amname: form.accountManagerId || undefined,
            outreach_am: form.outreachSpecialistId || undefined,
            published_date: form.publishedDate.toString(),
            contact_name: form.contactName.trim(),
            contact_email: form.contactEmail.trim(),
            category: form.category.trim(),
            publication: form.publication.trim(),
            article_link: form.articleLink.trim(),
            type: form.type,
            commission_rate: form.commissionRate || undefined,
            tags: form.tags,
            email_notification: form.emailNotification,
        };
        try {
            if (mode === "edit" && (initialArticle?._id || initialArticle?.id)) {
                const result = await dispatch(updatePublishedArticle({
                    id: initialArticle._id || initialArticle.id,
                    ...payload,
                    client_id: form.clientId,
                    closed_by: form.closedById || undefined,
                    am_id: form.accountManagerId || undefined,
                    outreach_specialist: form.outreachSpecialistId || undefined,
                }));
                if (updatePublishedArticle.fulfilled.match(result)) {
                    toast.success("Article information updated successfully.");
                    onSaved();
                    onClose();
                }
                else {
                    setErrorText(result.payload || "Failed to update published article.");
                }
            }
            else {
                const result = await dispatch(createPublishedArticle(payload));
                if (createPublishedArticle.fulfilled.match(result)) {
                    toast.success("Article information saved successfully.");
                    onSaved();
                    onClose();
                }
                else {
                    setErrorText(result.payload || "Failed to save published article.");
                }
            }
        }
        finally {
            setIsSubmitting(false);
        }
    }, [form, mode, initialArticle, dispatch, onSaved, onClose]);
    return (_jsxs(Dialog, { open: open, onClose: onClose, PaperProps: {
            sx: {
                borderRadius: "16px",
                width: "100%",
                maxWidth: "600px",
                m: 1,
                boxShadow: "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
            },
        }, children: [_jsxs(DialogTitle, { className: "all-Font-Sizes-16", sx: { display: "flex", justifyContent: "space-between", alignItems: "center", p: "24px", pb: "16px", color: "#101828", fontWeight: 600 }, children: [mode === "edit" ? "Edit Article" : mode === "copy" ? "Add Article (Copy)" : "Add Article", _jsx(IconButton, { onClick: onClose, size: "small", sx: { color: "#667085", "&:hover": { backgroundColor: "#F9FAFB" } }, children: _jsx(X, { size: 24 }) })] }), _jsx(DialogContent, { sx: { px: "24px", pb: "8px", pt: "16px", borderTop: "1px solid #EAECF0" }, children: _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 2, mt: 1 }, children: [_jsxs(Box, { sx: { display: "flex", gap: 2 }, children: [_jsx(Dropdown, { value: form.closedById, options: userOptions, onChange: (v) => setField("closedById", v), placeholder: "Select Closed By", searchable: true, usePortal: true, clearable: true }), _jsx(Dropdown, { value: form.accountManagerId, options: userOptions, onChange: (v) => setField("accountManagerId", v), placeholder: "Select Account Manager", searchable: true, usePortal: true, clearable: true })] }), _jsx(Dropdown, { value: form.outreachSpecialistId, options: outreachSpecialistOptions, onChange: (v) => setField("outreachSpecialistId", v), placeholder: "Select Outreach Specialist", searchable: true, usePortal: true, clearable: true }), _jsxs(Box, { sx: { display: "flex", gap: 2 }, children: [_jsx(Dropdown, { value: form.clientId, options: clientOptions, onChange: (v) => setField("clientId", v), placeholder: "Select Client", searchable: true, usePortal: true, clearable: true }), _jsx(SingleDatePicker, { value: form.publishedDate, onChange: (date) => setField("publishedDate", date), placeholder: "Published Date", allowToday: true, disableFuture: true })] }), _jsxs(Box, { sx: { display: "flex", gap: 2 }, children: [_jsx(Dropdown, { value: form.type, options: TYPE_OPTIONS, onChange: (v) => setField("type", v), placeholder: "Select Type", sortByLabel: false, usePortal: true, clearable: true }), _jsx(NumberInput, { value: form.commissionRate, onChange: (e) => setField("commissionRate", e.target.value), placeholder: "Commission Rate %", min: 0, max: 100 })] }), _jsxs(Box, { sx: { display: "flex", gap: 2 }, children: [_jsx(TextInput, { value: form.contactName, onChange: (e) => setField("contactName", e.target.value), placeholder: "Contact Name" }), _jsx(EmailInput, { value: form.contactEmail, onChange: (e) => setField("contactEmail", e.target.value), placeholder: "Contact Email" })] }), _jsxs(Box, { sx: { display: "flex", gap: 2 }, children: [_jsx(TextInput, { value: form.category, onChange: (e) => setField("category", e.target.value), placeholder: "Category" }), _jsx(TextInput, { value: form.publication, onChange: (e) => setField("publication", e.target.value), placeholder: "Publication" })] }), _jsx(TextInput, { value: form.articleLink, onChange: (e) => setField("articleLink", e.target.value), placeholder: "Article Link" }), _jsx(Dropdown, { value: form.tags, options: TAG_OPTIONS, onChange: (v) => setField("tags", v), placeholder: "Select Tag", multiple: true, sortByLabel: false, usePortal: true, clearable: true }), _jsxs(RadioGroup, { row: true, value: form.emailNotification, onChange: (e) => setField("emailNotification", e.target.value), children: [_jsx(FormControlLabel, { value: "email_affiliate_team", className: "all-Font-Sizes", label: "Notify Only Affiliate Team", control: _jsx(Radio, { sx: { color: "#FF008E", "&.Mui-checked": { color: "#FF008E" } } }) }), _jsx(FormControlLabel, { value: "email_team", className: "all-Font-Sizes", label: "Notify All", control: _jsx(Radio, { sx: { color: "#FF008E", "&.Mui-checked": { color: "#FF008E" } } }) })] }), errorText && (_jsx(Box, { sx: { color: "#d32f2f", fontSize: "13px", fontWeight: 500 }, children: errorText }))] }) }), _jsx(DialogActions, { sx: { pb: "32px", px: "24px", pt: 1, justifyContent: "flex-end" }, children: _jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", onClick: handleSave, loading: saving || isSubmitting, disabled: saving || isSubmitting, sx: { minWidth: "160px" }, children: saving || isSubmitting ? "Saving..." : "Save" }) })] }));
}
